/**
 * My Playlists Page
 * Displays user's playlists with course collections
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePlaylistStore } from '../stores/playlistStore';
import { Playlist } from '../services/playlistService';
import ConfirmModal from '../components/common/ConfirmModal';
import { getSmallThumbnailUrl } from '../utils/mediaUrl';
import './MyPlaylistsPage.css';

const MyPlaylistsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['playlist', 'common']);
  const {
    playlists,
    loading,
    error,
    fetchMyPlaylists,
    createPlaylist,
    deletePlaylist,
  } = usePlaylistStore();

  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [playlistToDelete, setPlaylistToDelete] = useState<Playlist | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchMyPlaylists();
  }, [fetchMyPlaylists]);

  const handleCreatePlaylist = async () => {
    if (!newTitle.trim()) {
      setCreateError(t('playlist:nameRequired'));
      return;
    }

    try {
      setCreateError(null);
      await createPlaylist({ title: newTitle.trim() });
      setNewTitle('');
      setIsCreating(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t('playlist:createFailed'));
    }
  };

  const handleDeleteClick = (playlist: Playlist, e: React.MouseEvent) => {
    e.stopPropagation();
    setPlaylistToDelete(playlist);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!playlistToDelete) return;

    setIsDeleting(true);
    try {
      await deletePlaylist(playlistToDelete.id);
      setDeleteModalOpen(false);
      setPlaylistToDelete(null);
    } catch (err) {
      console.error('Failed to delete playlist:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setPlaylistToDelete(null);
  };

  const getCourseCount = (playlist: Playlist): number => {
    return playlist.playlistCourses?.length || 0;
  };

  const getFirstCourseThumbnail = (playlist: Playlist): string | null => {
    const firstCourse = playlist.playlistCourses?.[0]?.course;
    const url = firstCourse?.coverImageUrl || firstCourse?.thumbnailUrl || null;
    return url ? getSmallThumbnailUrl(url) : null;
  };

  if (loading && playlists.length === 0) {
    return (
      <div className="playlists-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('playlist:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="playlists-page">
      <div className="playlists-header">
        <h1>{t('playlist:title')}</h1>
        {!isCreating && (
          <button
            className="btn-create-playlist"
            onClick={() => setIsCreating(true)}
          >
            {t('playlist:createPlaylist')}
          </button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => fetchMyPlaylists()}>{t('playlist:retry')}</button>
        </div>
      )}

      {isCreating && (
        <div className="create-playlist-form">
          <input
            type="text"
            placeholder={t('playlist:namePlaceholder')}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreatePlaylist();
              if (e.key === 'Escape') {
                setIsCreating(false);
                setNewTitle('');
              }
            }}
            autoFocus
            maxLength={100}
          />
          {createError && <p className="create-error">{createError}</p>}
          <div className="create-actions">
            <button
              className="btn-cancel"
              onClick={() => {
                setIsCreating(false);
                setNewTitle('');
                setCreateError(null);
              }}
            >
              {t('common:cancel')}
            </button>
            <button
              className="btn-save"
              onClick={handleCreatePlaylist}
              disabled={!newTitle.trim()}
            >
              {t('playlist:create')}
            </button>
          </div>
        </div>
      )}

      {playlists.length === 0 && !isCreating ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>{t('playlist:emptyTitle')}</h3>
          <p>{t('playlist:emptyDescription')}</p>
          <button
            className="btn-primary"
            onClick={() => setIsCreating(true)}
          >
            {t('playlist:createFirst')}
          </button>
        </div>
      ) : (
        <div className="playlists-grid">
          {playlists.map((playlist) => {
            const count = getCourseCount(playlist);
            return (
              <div
                key={playlist.id}
                className="playlist-card"
                onClick={() => navigate(`/playlists/${playlist.id}`)}
              >
                <div className="playlist-thumbnail">
                  {(() => {
                    const thumbnail = getFirstCourseThumbnail(playlist);
                    return thumbnail ? (
                      <img src={thumbnail} alt="" />
                    ) : (
                      <div className="thumbnail-placeholder">
                        <span>📚</span>
                      </div>
                    );
                  })()}
                  <div className="playlist-count">
                    {count === 1
                      ? t('playlist:courseCountSingular', { count })
                      : t('playlist:courseCount', { count })}
                  </div>
                </div>
                <div className="playlist-info">
                  <h3>{playlist.title}</h3>
                  {playlist.description && (
                    <p className="playlist-description">{playlist.description}</p>
                  )}
                  <div className="playlist-meta">
                    {playlist.isPublic ? (
                      <span className="visibility-badge public">{t('playlist:public')}</span>
                    ) : (
                      <span className="visibility-badge private">{t('playlist:private')}</span>
                    )}
                  </div>
                </div>
                <button
                  className="btn-delete"
                  onClick={(e) => handleDeleteClick(playlist, e)}
                  title={t('playlist:deletePlaylist')}
                  aria-label={t('playlist:deletePlaylist')}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        title={t('playlist:deleteTitle')}
        message={t('playlist:deleteMessage', { title: playlistToDelete?.title })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={isDeleting}
      />
    </div>
  );
};

export default MyPlaylistsPage;
